import { useState, useEffect, useRef, useCallback, memo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  ArrowRight,
  Eye,
  Share2,
  Heart,
  Volume2,
  VolumeX,
  ShoppingBag,
  Check,
} from "lucide-react";
import ProductBottomSheet from "../components/ProductBottomSheet";
import { trackProductView, trackSwipeDepth, trackBuyClick } from "../utils/analytics";
import { useCart } from "../context/CartContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCount = (n) => {
  if (n >= 1000) return (n / 1000).toFixed(1) + "k";
  return String(n);
};

// ─── Real countdown hook (conditional — only ticks when active) ─────
const useCountdown = (endTimeISO, shouldTick = true) => {
  const [remaining, setRemaining] = useState(() => {
    if (!endTimeISO) return { text: "", expired: true };
    const diff = Math.floor((new Date(endTimeISO) - Date.now()) / 1000);
    if (diff <= 0) return { text: "Завершено", expired: true };
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    const pad = (n) => n.toString().padStart(2, "0");
    return { text: `${pad(h)}:${pad(m)}:${pad(s)}`, expired: false };
  });

  useEffect(() => {
    if (!endTimeISO || !shouldTick) return;
    const endMs = new Date(endTimeISO).getTime();

    const tick = () => {
      const diff = Math.floor((endMs - Date.now()) / 1000);
      if (diff <= 0) {
        setRemaining({ text: "Завершено", expired: true });
        return;
      }
      const h = Math.floor(diff / 3600);
      const m = Math.floor((diff % 3600) / 60);
      const s = diff % 60;
      const pad = (n) => n.toString().padStart(2, "0");
      setRemaining({ text: `${pad(h)}:${pad(m)}:${pad(s)}`, expired: false });
    };

    tick();
    const timer = setInterval(tick, 1000);
    return () => clearInterval(timer);
  }, [endTimeISO, shouldTick]);

  return remaining;
};

// ─── Single product card (image or video) ──────────────────────────────
// ─── localStorage / sessionStorage helpers ─────────────────────────────
const LIKES_KEY = "shopthing-likes";
const VIEWS_KEY = "shopthing-views";

const getLikedProducts = () => {
  try { return JSON.parse(localStorage.getItem(LIKES_KEY)) || {}; } catch { return {}; }
};
const setLikedProducts = (map) => localStorage.setItem(LIKES_KEY, JSON.stringify(map));

const getViewedProducts = () => {
  try { return JSON.parse(sessionStorage.getItem(VIEWS_KEY)) || {}; } catch { return {}; }
};
const setViewedProducts = (map) => sessionStorage.setItem(VIEWS_KEY, JSON.stringify(map));

const ProductCard = memo(({
  product,
  onMoreClick,
  onAddToCart,
  navigateBack,
  isActive,
  isNear,
  soundEnabled,
  onToggleSound,
  eventEndTime,
  cartCount,
  onOpenCart,
}) => {
  const [liked, setLiked] = useState(() => !!getLikedProducts()[product.id]);
  const [viewedThisSession, setViewedThisSession] = useState(() => !!getViewedProducts()[product.id]);
  const [videoFailed, setVideoFailed] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const videoRef = useRef(null);
  const { text: countdownText, expired } = useCountdown(eventEndTime, isActive || isNear);

  const baseLikes = product.likes;
  const baseViews = product.views;
  const displayedLikes = baseLikes + (liked ? 1 : 0);
  const displayedViews = baseViews + (viewedThisSession ? 1 : 0);

  const hasVideo = product.mediaType === "video" && product.videoUrl;
  const showVideo = hasVideo && !videoFailed;
  const shouldLoad = showVideo && (isActive || isNear);

  // Play/pause and mute — src is set declaratively via JSX
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;

    if (isActive) {
      video.muted = !soundEnabled;
      const p = video.play();
      if (p && p.catch) {
        p.catch(() => {
          if (!video.muted) {
            video.muted = true;
            video.play().catch(() => {});
          }
        });
      }
    } else {
      video.pause();
    }
  }, [isActive, soundEnabled, showVideo]);

  // Unload video when far away to save memory
  useEffect(() => {
    const video = videoRef.current;
    if (!video || !showVideo) return;
    if (!shouldLoad) {
      video.pause();
      video.removeAttribute("src");
      video.load();
    }
  }, [shouldLoad, showVideo]);

  // Track view: mark product as viewed once per session when it becomes active
  useEffect(() => {
    if (isActive && !viewedThisSession) {
      setViewedThisSession(true);
      const viewed = getViewedProducts();
      viewed[product.id] = true;
      setViewedProducts(viewed);
    }
  }, [isActive, viewedThisSession, product.id]);

  const handleLike = () => {
    const nowLiked = !liked;
    setLiked(nowLiked);
    const map = getLikedProducts();
    if (nowLiked) {
      map[product.id] = true;
    } else {
      delete map[product.id];
    }
    setLikedProducts(map);
  };

  // Sound toggle — called in a user-gesture context
  const handleSoundToggle = (e) => {
    e.stopPropagation();
    const video = videoRef.current;
    if (video) {
      video.muted = soundEnabled; // Toggling: was on→off, was off→on
      if (!soundEnabled) {
        video.play().catch(() => {});
      }
    }
    onToggleSound();
  };

  const handleVideoError = () => {
    if (isActive || isNear) {
      setVideoFailed(true);
    }
  };

  return (
    <div
      className="snap-section relative bg-[#0B0B0B]"
      data-testid={`product-card-${product.id}`}
    >
      {/* ── Media layer ── */}
      {showVideo ? (
        <video
          ref={videoRef}
          src={shouldLoad ? product.videoUrl : undefined}
          className="absolute inset-0 w-full h-full object-cover"
          loop
          muted
          playsInline
          onError={handleVideoError}
          data-testid={`product-video-${product.id}`}
        />
      ) : (
        product.image ? (
          <img
            src={product.image}
            alt={product.name}
            className="absolute inset-0 w-full h-full object-cover"
            data-testid={`product-image-${product.id}`}
          />
        ) : (
          <div
            className="absolute inset-0 bg-[#0B0B0B]"
            data-testid={`product-placeholder-${product.id}`}
          />
        )
      )}

      {/* ── Sound toggle (video only, active only) ── */}
      {showVideo && isActive && (
        <button
          onClick={handleSoundToggle}
          className="absolute top-24 right-4 z-30 w-9 h-9 flex items-center justify-center rounded-full bg-black/40 backdrop-blur-sm transition-opacity"
          data-testid="sound-toggle"
        >
          {soundEnabled ? (
            <Volume2 size={16} className="text-white icon-shadow" />
          ) : (
            <VolumeX size={16} className="text-white icon-shadow" />
          )}
        </button>
      )}

      {/* Top gradient */}
      <div className="absolute top-0 left-0 right-0 h-32 bg-gradient-to-b from-black/60 to-transparent z-10" />

      {/* Top area: back + event title + cart */}
      <div className="absolute top-0 left-0 right-0 pt-12 pb-4 px-4 z-20">
        <div className="flex items-center justify-center relative">
          <button
            className="absolute left-0 p-2 text-white icon-shadow"
            data-testid="back-button"
            onClick={() => navigateBack()}
          >
            <ChevronLeft size={24} strokeWidth={2} />
          </button>
          <div className="text-center">
            <h1
              className="text-white text-sm font-semibold font-sans tracking-wide text-shadow"
              data-testid="event-title"
            >
              {product.eventTitle}
            </h1>
            <div className="mt-1 flex items-center justify-center gap-1">
              <span className={`text-xs font-sans tracking-wide ${expired ? "text-white/50" : "text-white/80"}`}>
                {expired ? countdownText : `${countdownText} ост.`}
              </span>
              <ChevronLeft
                size={12}
                className="text-white/60 rotate-[-90deg]"
              />
            </div>
          </div>
          <button
            className="absolute right-0 p-2 text-white icon-shadow"
            data-testid="feed-cart-button"
            onClick={onOpenCart}
          >
            <ShoppingBag size={22} strokeWidth={1.8} />
            {cartCount > 0 && (
              <span className="absolute top-0 right-0 min-w-[18px] h-[18px] px-1 rounded-full bg-[#D8C08F] text-black text-[10px] font-sans font-bold flex items-center justify-center">
                {cartCount > 99 ? "99+" : cartCount}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Right social column */}
      <div
        className="absolute right-4 bottom-44 flex flex-col items-center gap-5 z-20"
        data-testid="social-column"
      >
        <div className="flex flex-col items-center gap-1">
          <div className="w-10 h-10 flex items-center justify-center">
            <Eye
              size={24}
              className="text-white icon-shadow"
              strokeWidth={1.5}
            />
          </div>
          <span
            className="text-white text-xs font-sans icon-shadow"
            data-testid="views-count"
          >
            {formatCount(displayedViews)}
          </span>
        </div>
        <div className="flex flex-col items-center gap-1">
          <button
            className="w-10 h-10 flex items-center justify-center"
            data-testid="share-button"
          >
            <Share2
              size={22}
              className="text-white icon-shadow"
              strokeWidth={1.5}
            />
          </button>
        </div>
        <div className="flex flex-col items-center gap-1">
          <button
            className="w-10 h-10 flex items-center justify-center"
            onClick={handleLike}
            data-testid="like-button"
          >
            <Heart
              size={24}
              className={`icon-shadow transition-colors ${
                liked ? "text-[#D84C3F] fill-[#D84C3F]" : "text-white"
              }`}
              strokeWidth={1.5}
            />
          </button>
          <span
            className="text-white text-xs font-sans icon-shadow"
            data-testid="likes-count"
          >
            {formatCount(displayedLikes)}
          </span>
        </div>
      </div>

      {/* Bottom gradient */}
      <div className="absolute bottom-0 left-0 right-0 h-64 bg-gradient-to-t from-[#0B0B0B] via-[#0B0B0B]/80 to-transparent z-10" />

      {/* Bottom text overlay */}
      <div
        className="absolute bottom-24 left-0 right-0 px-5 z-20"
        data-testid="product-info"
      >
        <div className="flex justify-between items-end">
          <div className="flex-1 pr-4">
            <p
              className="text-white/80 text-xs font-serif uppercase tracking-[0.2em] mb-1"
              data-testid="product-brand"
            >
              {product.brand}
            </p>
            <h2
              className="text-white text-base font-sans font-medium leading-tight mb-2"
              data-testid="product-name"
            >
              {product.name}
            </h2>
            <div className="flex items-center gap-2">
              <span
                className="text-[#A7A7A7] text-sm line-through font-sans"
                data-testid="retail-price"
              >
                Ритейл: {product.retailPrice.toFixed(2)} ₽
              </span>
              <span className="text-white mx-1">|</span>
              <span
                className="text-white text-base font-semibold font-sans"
                data-testid="sale-price"
              >
                {product.salePrice.toFixed(2)} ₽
              </span>
            </div>
          </div>
          <button
            onClick={() => onMoreClick(product)}
            className="text-white text-sm font-sans font-medium tracking-wide pb-1"
            data-testid="more-button"
          >
            Детали
          </button>
        </div>
      </div>

      {/* Add-to-cart button */}
      <div className="absolute bottom-6 left-5 right-5 z-20">
        <button
          onClick={() => {
            if (expired) return;
            onAddToCart(product);
            setJustAdded(true);
            setTimeout(() => setJustAdded(false), 900);
          }}
          disabled={expired}
          className={`w-full font-semibold py-4 rounded-full flex items-center justify-center gap-2 font-sans text-sm tracking-wide ${
            expired
              ? "bg-[#555] text-white/60 cursor-not-allowed"
              : justAdded
              ? "bg-emerald-600 text-white"
              : "bg-[#D8C08F] text-black active:scale-[0.98] transition-transform"
          }`}
          data-testid="add-to-cart-button"
        >
          {expired ? (
            "Акция завершена"
          ) : justAdded ? (
            <>
              <Check size={16} strokeWidth={2.5} /> Добавлено
            </>
          ) : (
            "Добавить в корзину"
          )}
        </button>
      </div>
    </div>
  );
});
ProductCard.displayName = "ProductCard";

// ─── Main product feed (event-scoped, horizontal swipe) ────────────────
const ProductFeed = () => {
  const { eventId } = useParams();
  const [products, setProducts] = useState([]);
  const [eventData, setEventData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(false);
  const [loadPhase, setLoadPhase] = useState("loading");
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [showEntryHint, setShowEntryHint] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(false);
  const containerRef = useRef(null);
  const navigate = useNavigate();
  const { addItem, itemsCount } = useCart();

  const fetchData = useCallback(() => {
    setLoading(true);
    setFetchError(false);
    setLoadPhase("loading");

    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 14000);

    Promise.all([
      fetch(`${API_URL}/api/events/${eventId}`, { signal: controller.signal }).then((r) => {
        if (!r.ok) throw new Error(`Event HTTP ${r.status}`);
        return r.json();
      }),
      fetch(`${API_URL}/api/events/${eventId}/products`, { signal: controller.signal }).then((r) => {
        if (!r.ok) throw new Error(`Products HTTP ${r.status}`);
        return r.json();
      }),
    ])
      .then(([event, prods]) => {
        setEventData(event);
        setProducts(prods);
      })
      .catch((err) => {
        if (err.name !== "AbortError") console.error("Failed to fetch:", err);
        setFetchError(true);
        setLoadPhase("failed");
      })
      .finally(() => {
        clearTimeout(timeout);
        setLoading(false);
      });

    return () => { clearTimeout(timeout); controller.abort(); };
  }, [eventId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Staged timeout messages
  useEffect(() => {
    if (!loading) return;
    const slowTimer = setTimeout(() => setLoadPhase("slow"), 7000);
    const failTimer = setTimeout(() => {
      setLoadPhase("failed");
      setLoading(false);
      setFetchError(true);
    }, 14000);
    return () => { clearTimeout(slowTimer); clearTimeout(failTimer); };
  }, [loading]);

  const navigateBack = useCallback(() => navigate("/"), [navigate]);

  const maxIndexRef = useRef(0);
  const scrollRafRef = useRef(null);

  // Throttled scroll handler using rAF — fires at most once per frame
  const handleScroll = useCallback(() => {
    if (scrollRafRef.current) return;
    scrollRafRef.current = requestAnimationFrame(() => {
      const el = containerRef.current;
      if (el) {
        const idx = Math.round(el.scrollLeft / el.offsetWidth);
        setCurrentIndex(idx);
      }
      scrollRafRef.current = null;
    });
  }, []);

  // Show entry hint only once per event
  useEffect(() => {
    if (!loading && products.length > 1) {
      const key = `swipe-hint-${eventId}`;
      if (!localStorage.getItem(key)) {
        setShowEntryHint(true);
      }
    }
  }, [eventId, products.length, loading]);

  // Dismiss on first swipe
  useEffect(() => {
    if (currentIndex > 0 && showEntryHint) {
      setShowEntryHint(false);
      localStorage.setItem(`swipe-hint-${eventId}`, "1");
    }
  }, [currentIndex, showEntryHint, eventId]);

  // Auto-dismiss after animation
  useEffect(() => {
    if (!showEntryHint) return;
    const t = setTimeout(() => {
      setShowEntryHint(false);
      localStorage.setItem(`swipe-hint-${eventId}`, "1");
    }, 3200);
    return () => clearTimeout(t);
  }, [showEntryHint, eventId]);

  // Track product_view and swipe_depth when currentIndex changes
  useEffect(() => {
    if (loading || products.length === 0) return;
    const product = products[currentIndex];
    if (!product) return;

    // Track product view for the active product
    trackProductView(product.id, eventId, currentIndex);

    // Track swipe depth (only if this is a new max)
    if (currentIndex > maxIndexRef.current) {
      maxIndexRef.current = currentIndex;
      trackSwipeDepth(eventId, currentIndex);
    }
  }, [currentIndex, loading, products, eventId]);

  const handleMoreClick = useCallback((product) => {
    setSelectedProduct(product);
    setIsDrawerOpen(true);
  }, []);

  const handleAddToCart = useCallback((product) => {
    trackBuyClick(product.id, eventId, product.name, product.salePrice);
    // If multiple sizes — open detail sheet for size selection
    if (product.sizes && product.sizes.length > 1) {
      setSelectedProduct(product);
      setIsDrawerOpen(true);
      return;
    }
    const defaultSize = product.sizes && product.sizes.length === 1 ? product.sizes[0] : null;
    addItem(product, { size: defaultSize, quantity: 1 });
  }, [eventId, addItem]);

  const handleOpenCart = useCallback(() => navigate("/cart"), [navigate]);

  const handleCloseDrawer = useCallback(() => {
    setIsDrawerOpen(false);
    setTimeout(() => setSelectedProduct(null), 300);
  }, []);

  const handleToggleSound = useCallback(() => setSoundEnabled((prev) => !prev), []);

  // Error / retry state
  if (fetchError && !loading) {
    return (
      <div
        className="w-full h-[100dvh] bg-[#0B0B0B] flex flex-col items-center justify-center gap-4 px-6"
        data-testid="product-feed-error"
      >
        <button
          onClick={() => navigate("/")}
          className="absolute top-12 left-4 p-2 text-white/70"
          data-testid="error-back-button"
        >
          <ChevronLeft size={24} strokeWidth={2} />
        </button>
        <p className="text-white/60 text-sm font-sans text-center leading-relaxed">
          Не удалось загрузить страницу.<br />Попробуйте ещё раз.
        </p>
        <button
          onClick={() => fetchData()}
          className="px-8 py-3 bg-[#D8C08F] text-black text-sm font-sans font-medium rounded-full active:scale-[0.97] transition-transform"
          data-testid="retry-button"
        >
          Повторить
        </button>
      </div>
    );
  }

  // Loading state with staged messages
  if (loading) {
    return (
      <div
        className="w-full h-[100dvh] bg-[#0B0B0B] flex flex-col items-center justify-center gap-3"
        data-testid="product-feed-loading"
      >
        <div className="w-6 h-6 border-2 border-white/20 border-t-white/80 rounded-full animate-spin" />
        {loadPhase === "slow" && (
          <p
            className="text-white/40 text-xs font-sans mt-2"
            data-testid="slow-loading-message"
          >
            Загрузка занимает больше времени, чем обычно
          </p>
        )}
      </div>
    );
  }

  return (
    <div
      className="w-full h-[100dvh] bg-[#0B0B0B] overflow-hidden relative"
      data-testid="product-feed"
    >
      {/* Horizontal snap-scroll container */}
      <div
        ref={containerRef}
        className="snap-container scrollbar-hide"
        onScroll={handleScroll}
        data-testid="scroll-container"
      >
        {products.map((product, index) => (
          <ProductCard
            key={product.id}
            product={product}
            isActive={index === currentIndex}
            isNear={Math.abs(index - currentIndex) <= 1}
            soundEnabled={soundEnabled}
            onToggleSound={handleToggleSound}
            onMoreClick={handleMoreClick}
            onAddToCart={handleAddToCart}
            navigateBack={navigateBack}
            eventEndTime={eventData?.eventEndTime}
            cartCount={itemsCount}
            onOpenCart={handleOpenCart}
          />
        ))}
      </div>

      {/* Entry-only swipe hint */}
      {showEntryHint && (
        <div
          className="absolute right-5 top-1/2 -translate-y-1/2 z-30 pointer-events-none"
          data-testid="swipe-entry-hint"
        >
          <div className="swipe-entry-hint">
            <ArrowRight
              size={22}
              className="text-white drop-shadow-[0_0_6px_rgba(255,255,255,0.35)]"
              strokeWidth={1.5}
            />
          </div>
        </div>
      )}

      {/* Dot indicators */}
      {products.length > 1 && (
        <div
          className="absolute bottom-[82px] left-1/2 -translate-x-1/2 z-30 flex gap-1.5 pointer-events-none"
          data-testid="dot-indicators"
        >
          {products.map((_, i) => (
            <div
              key={i}
              className={`rounded-full transition-all duration-300 ${
                i === currentIndex
                  ? "w-1.5 h-1.5 bg-white/80"
                  : "w-1.5 h-1.5 bg-white/25"
              }`}
              data-testid={`dot-${i}`}
            />
          ))}
        </div>
      )}

      {/* Bottom Sheet */}
      <ProductBottomSheet
        product={selectedProduct}
        isOpen={isDrawerOpen}
        onClose={handleCloseDrawer}
        eventEndTime={eventData?.eventEndTime}
      />
    </div>
  );
};

export default ProductFeed;
